/**
 * ConversationList Component
 *
 * Displays list of conversations with search and filters
 */

import { formatDistanceToNow } from "date-fns";
import React, { useState } from "react";
import { ConversationListItem } from "../../../store/api/conversationsApi";

interface ConversationListProps {
	conversations: ConversationListItem[];
	selectedConversationId: string | null;
	onSelectConversation: (id: string) => void;
	onNewConversation: () => void;
	isLoading: boolean;
}

export const ConversationList: React.FC<ConversationListProps> = ({
	conversations,
	selectedConversationId,
	onSelectConversation,
	onNewConversation,
	isLoading,
}) => {
	const [searchQuery, setSearchQuery] = useState("");

	// Filter conversations by search query
	const filteredConversations = conversations.filter((conv) =>
		conv.subject.toLowerCase().includes(searchQuery.toLowerCase()),
	);

	return (
		<div className="flex flex-col h-full bg-white overflow-hidden">
			{/* Header */}
			<div className="p-4 border-b border-gray-200 flex-shrink-0">
				<div className="flex items-center justify-between mb-4">
					<h2 className="text-xl font-semibold text-gray-900">Messages</h2>
					<button
						onClick={onNewConversation}
						className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
					>
						<svg
							className="w-5 h-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 4v16m8-8H4"
							/>
						</svg>
						New
					</button>
				</div>

				{/* Search */}
				<div className="relative">
					<input
						type="text"
						placeholder="Search conversations..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
					<svg
						className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
						/>
					</svg>
				</div>
			</div>

			{/* Conversation List */}
			<div className="flex-1 overflow-y-auto">
				{isLoading ? (
					<div className="p-4 space-y-3">
						{[...Array(5)].map((_, i) => (
							<div
								key={i}
								className="p-4 border-b border-gray-100 animate-pulse"
							>
								<div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
								<div className="h-3 bg-gray-200 rounded w-1/2"></div>
							</div>
						))}
					</div>
				) : filteredConversations.length === 0 ? (
					<div className="flex flex-col items-center justify-center h-full p-8 text-center">
						<svg
							className="w-16 h-16 text-gray-300 mb-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
							/>
						</svg>
						<p className="text-gray-500 font-medium mb-2">
							{searchQuery ? "No conversations found" : "No conversations yet"}
						</p>
						<p className="text-sm text-gray-400 mb-4">
							{searchQuery
								? "Try a different search term"
								: "Start a new conversation to get started"}
						</p>
						{!searchQuery && (
							<button
								onClick={onNewConversation}
								className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
							>
								Start Conversation
							</button>
						)}
					</div>
				) : (
					<div className="overflow-y-auto flex-1 min-h-0">
						{filteredConversations.map((conversation) => (
							<button
								key={conversation.id}
								onClick={() => onSelectConversation(conversation.id)}
								className={`w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
									selectedConversationId === conversation.id
										? "bg-blue-50 border-l-4 border-l-blue-600"
										: ""
								}`}
							>
								<div className="flex items-start justify-between mb-1">
									<h3 className="font-semibold text-gray-900 truncate pr-2">
										{conversation.subject}
									</h3>
									{conversation.last_message_at && (
										<span className="text-xs text-gray-500 whitespace-nowrap">
											{formatDistanceToNow(
												new Date(conversation.last_message_at),
												{
													addSuffix: true,
												},
											)}
										</span>
									)}
								</div>
								<div className="flex items-center gap-2">
									<span className="text-sm text-gray-500">
										{conversation.participant_count} participant
										{conversation.participant_count !== 1 ? "s" : ""}
									</span>
								</div>
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	);
};
